// Σταθερές που μοιράζονται ο service worker και τα παράθυρα της επέκτασης.
//
// Ζουν σε δικό τους αρχείο ώστε το popup να μη χρειάζεται να εισαγάγει τον
// πελάτη του API. Το όριο είναι σκόπιμο: μόνο ο service worker κάνει αιτήματα.

/** Τα όρια των σχημάτων επικύρωσης του API. Κόβουμε πριν στείλουμε. */
// Τα όρια αντιγράφουν τα σχήματα επικύρωσης του API (`api/lib/schemas.js` και
// `api/routes/`). Η επέκταση **περικόπτει** στα όρια αυτά πριν στείλει, ώστε ο
// χρήστης να μη χάνει ό,τι πληκτρολόγησε σε ένα 422.
//
// Το `url` και το `image` ανέβηκαν από 2048 σε 4096 μαζί με το `URL_MAX` του API
// (17/08/2026). Αν έμεναν στα 2048 η ασυμφωνία δεν θα έδινε σφάλμα — θα ήταν
// χειρότερη: η επέκταση θα **περιέκοπτε σιωπηλά** μια διεύθυνση που το API δέχεται
// ολόκληρη, και θα αποθηκευόταν σπασμένη διεύθυνση αντί να απορριφθεί.
export const LIMITS = Object.freeze({
  title: 300,
  description: 2000,
  url: 4096,
  image: 4096,
  tag: 60,
  tags: 30
});

/**
 * ΛΑ-29: τα **έξι** πεδία χρηματοδότησης, ως ένας κατάλογος.
 *
 * **Γιατί ένας και όχι τρεις.** Τα πεδία χρειάζονται τρία πράγματα — όνομα, είδος
 * ελέγχου, ανώτατο μήκος — και η εύκολη μορφή θα ήταν τρεις παράλληλοι κατάλογοι:
 * ένας για τη σήμανση, ένας για τα όρια, ένας για τις ετικέτες. Τρεις κατάλογοι
 * που πρέπει να συμφωνούν είναι **η υπογραφή** που έχει ήδη κοστίσει σε αυτή την
 * εργασία: η σταθερά σε τρία πακέτα, ο κατάλογος γεγονότων σε τρεις κλάδους, ο
 * πίνακας αναγνωριστικών του popup απέναντι στη σήμανσή του.
 *
 * Εδώ ο κατάλογος είναι **ένας** και τα πεδία της φόρμας **παράγονται** από αυτόν,
 * οπότε η σήμανση δεν μπορεί να αποκλίνει: δεν υπάρχει σήμανση να αποκλίνει.
 * Οι **ετικέτες** ζουν στο `strings.js`, όπου ζει κάθε κείμενο, με τα **ίδια
 * κλειδιά** — και η αντιστοιχία ελέγχεται, δεν δηλώνεται.
 *
 * **Ο κύριος αυτού του καταλόγου είναι το API**, όχι η επέκταση: τα ονόματα και τα
 * μήκη είναι του `fundingInputSchema` (`api/lib/schemas.js`). Ο έλεγχος τα διαβάζει
 * από εκεί και τα αντιπαραβάλλει, ώστε η αντιγραφή —αναπόφευκτη, γιατί η επέκταση
 * δεν έχει εξαρτήσεις κατά σχεδίαση (MV3)— να μην μπορεί να αποκλίνει σιωπηλά.
 *
 * Το `deadline` δεν έχει `max`: έχει **μορφή** (`YYYY-MM-DD`), όχι μήκος. Το
 * `amount` είναι αριθμός. Όριο που δεν υπάρχει δεν εφευρίσκεται.
 */
export const FUNDING_FIELDS = Object.freeze([
  Object.freeze({ key: 'agency', kind: 'text', max: 300 }),
  Object.freeze({ key: 'field', kind: 'text', max: 300 }),
  Object.freeze({ key: 'deadline', kind: 'date' }),
  Object.freeze({ key: 'amount', kind: 'amount' }),
  Object.freeze({ key: 'eligibleEntities', kind: 'textarea', rows: 2, max: 1000 }),
  Object.freeze({ key: 'terms', kind: 'textarea', rows: 3, max: 2000 })
]);

/**
 * Το κενό πρόχειρο χρηματοδότησης: **και τα έξι κλειδιά, πάντοτε συμβολοσειρές**.
 *
 * Η μορφή δεν είναι αυθαίρετη — είναι το συμβόλαιο του API, μετρημένο στο σχήμα:
 * η **κενή συμβολοσειρά** γίνεται «καμία τιμή», ενώ το `null` δίνει **422** και σε
 * κείμενο και σε αριθμό. Ειδικά για το ποσό, η κενή συμβολοσειρά **δεν** γίνεται
 * μηδέν, και αυτό είναι ρητός κανόνας του σχήματος και όχι παρενέργεια.
 *
 * Ίδιο σχήμα με το `EMPTY_FUNDING` της διαδικτυακής εφαρμογής, για τον ίδιο λόγο:
 * το πρόχειρο έχει **πάντα** και τα έξι κλειδιά, ανεξάρτητα από το τι έστειλε ή δεν
 * έστειλε ο εξυπηρετητής.
 */
export const EMPTY_FUNDING = Object.freeze(
  Object.fromEntries(FUNDING_FIELDS.map((field) => [field.key, '']))
);

/**
 * Κωδικοί σφάλματος. Οι πρώτοι έρχονται από το API (Απόφαση D4), οι υπόλοιποι
 * παράγονται μέσα στην επέκταση. Το popup αποφασίζει τη συμπεριφορά του από
 * τον κωδικό και ποτέ από το κείμενο του μηνύματος.
 */
export const ERRORS = Object.freeze({
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  VALIDATION: 'VALIDATION_ERROR',
  RATE_LIMITED: 'RATE_LIMITED',

  NETWORK: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  BAD_RESPONSE: 'BAD_RESPONSE',
  UNEXPECTED: 'UNEXPECTED',

  NO_COLLECTION: 'NO_COLLECTION',
  TITLE_REQUIRED: 'TITLE_REQUIRED',
  UNSUPPORTED_PAGE: 'UNSUPPORTED_PAGE',
  ADDRESS_TOO_LONG: 'ADDRESS_TOO_LONG'
});

export default { LIMITS, ERRORS, FUNDING_FIELDS, EMPTY_FUNDING };
